'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import type { ButtonModalBlock as ButtonModalBlockType, PageSettings } from '@shared/types/salespads';
import type { EventData } from '@/lib/fetch-event-data';
import { pushDataLayer } from '@/lib/track-gtm';

// Lazy-load BookingWidget only when the modal actually opens.
const BookingWidget = dynamic(
  () => import('../booking/BookingWidget').then(m => ({ default: m.BookingWidget })),
  { ssr: false, loading: () => (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  )}
);

const sizeMap: Record<string, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};

const alignMap: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

interface Props {
  block: ButtonModalBlockType;
  settings: PageSettings;
  eventData?: EventData;
}

export function ButtonModalBlock({ block, settings, eventData }: Props) {
  const [open, setOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState<string>('form');

  // Preload booking widget JS after page settles so modal opens instantly
  useEffect(() => {
    const timer = setTimeout(() => {
      import('../booking/BookingWidget').catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const { data } = block;
  const size = sizeMap[data.size] || sizeMap.md;
  const align = alignMap[data.align] || alignMap.center;
  const buttonColor = data.color || settings.colorPrimary;

  let className = `inline-block rounded-lg font-semibold transition-all cursor-pointer ${size}`;
  const style: React.CSSProperties = {};

  const isGradient = buttonColor.includes('gradient');

  if (data.style === 'primary') {
    className += ' text-white hover:opacity-90';
    style.background = buttonColor;
  } else if (data.style === 'secondary') {
    className += ' bg-gray-100 text-gray-900 hover:bg-gray-200';
  } else if (data.style === 'outline') {
    className += ' border-2 bg-transparent hover:opacity-80';
    if (isGradient) {
      style.borderImage = `${buttonColor} 1`;
      style.background = buttonColor;
      style.backgroundClip = 'text';
      style.WebkitBackgroundClip = 'text';
      style.color = 'transparent';
    } else {
      style.borderColor = buttonColor;
      style.color = buttonColor;
    }
  }

  if (data.textColor) {
    style.color = data.textColor;
  }

  if (data.fullWidth) {
    className += ' w-full text-center';
  }

  // Mirror the widget's own width rule exactly (applies to BOTH the standard
  // and slide form widgets — they share the rule): narrow (max-w-md) ONLY on a
  // calendar-less form step; every other state (calendar / confirmation /
  // disqualified, or a form with the calendar beside it) renders the wide
  // side-by-side layout. `bookingStep` is reported by both widgets via
  // onStepChange. Previously this keyed off `bookingStep === 'calendar'` (left
  // the confirmation step squashed) and excluded slide forms — but slide forms
  // DO need the wide modal when they show a calendar (omitting them squashed it).
  const showCalendarOnForm = eventData?.event.show_calendar_on_form ?? true;
  const needsWideModal = !!eventData && (
    bookingStep !== 'form' || showCalendarOnForm
  );

  return (
    <>
      <div className={`flex ${align} py-2`}>
        <button
          onClick={() => {
            pushDataLayer('salescal.salespad_click', {
              blockId: block.id,
              blockType: 'button-modal',
              label: data.text,
              url: '',
              clickAction: 'modal',
            });
            // Reset to the form step on (re)open so the modal opens at the
            // correct width — the widget always remounts on the form step.
            setBookingStep('form');
            setOpen(true);
          }}
          className={className}
          style={style}
        >
          {data.text}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative transition-all duration-300 ${needsWideModal ? 'max-w-4xl' : 'max-w-lg'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg width={20} height={20} className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
            {eventData ? (
              <BookingWidget
                event={eventData.event}
                fields={eventData.fields}
                ruleSets={eventData.ruleSets}
                profile={eventData.profile}
                userEmail={eventData.profile.email}
                onStepChange={setBookingStep}
                metaPixelIds={eventData.metaPixelIds}
                teamId={eventData.teamId}
                metaPixelEventNames={eventData.metaPixelEventNames}
                confirmationIncludeCancelReschedule={eventData.confirmationIncludeCancelReschedule}
                gtmContainerId={eventData.gtmContainerId}
                eventSlug={eventData.event.slug}
                bookingPaused={eventData.bookingPaused}
              />
            ) : (
              <div className="text-center py-12 text-gray-400">
                {data.eventId ? 'Event not found or not published' : 'No event selected'}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
